// third-party
import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// types
import { DefaultRootStateProps, MakeRequired } from 'types';
import { InventorySkuType, ProductMultiCatalog } from 'types/e-commerce';
import { CreateDetailProductType, MerchantProductType, SaveApprovalPayload } from 'types/product';
import {
    SaveFacetsProductsProps,
    SearchProductTypeProps,
    getCategoryFacetsProps,
    getProductsProps,
    getProductsThunkHistoryProps,
    getProductsToApproveCountProps,
    getProductsToApproveProps,
    metadata,
    SaveFacetsSkusProps
} from 'types/slices/slices';
import { uploadOneImageToSkuProps } from 'types/slices/slices';
import { EditDetailProductType, MerchantType, PricingItemType, skuImageType } from 'types/components/shared';

import { dispatch as dispatchSnackBar } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import JSZip from 'jszip';
import { AxiosResponse } from 'axios';
import { AllMerchantsProductDataType } from 'types/views/products/allMerchantsProductData';

const initialState: DefaultRootStateProps['product'] = {
    image: '',
    loadingFiles: false,
    files: null,
    error: null,
    productErrors: { product: [], sku: [] },
    products: [],
    product: null,
    relatedProducts: [],
    skus: [],
    loadingSku: true,
    loadingSkus: true,
    selectedSku: null,
    selectedSkus: [],
    categories: [],
    tradePolicies: [],
    approvalStatus: [],
    getRejectedStatus: [],
    savingApproval: false,
    approvalHistorial: [],
    reviews: [],
    addresses: [],
    loadingProduct: true,
    merchantProducts: [],
    createProductFacet: [],
    warehouses: [],
    loadingProducts: true,
    loadingMedia: false,
    productsMulticatalog: [],
    productsTotalPages: 0,
    productsFacetsMulti: [],
    uploadedImages: { done: [], pending: [], error: [] },
    totalImagesToLoad: 0,
    creatingProduct: false,
    urlSearch: '',
    productsToApprove: {
        products: [],
        totalCount: 0,
        totalPages: 0,
        loading: false
    },
    bulk: [],
    loadingBulk: false,
    productsToApproveCount: 0,
    loadingErrors: false,
    loadingResetErrors: false,
    productsMulticatalogVendors: { items: [], totalItems: 0 },
    loadingProductsVendors: false
};

const slice = createSlice({
    name: 'product',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        getProductsPending(state) {
            state.loadingProducts = true;
        },
        // FILTER PRODUCTS
        filterProductsSuccess(state, action) {
            state.products = action.payload;
        },

        // GET PRODUCT
        getProductSuccess(state, action) {
            state.product = action.payload;
        },
        // GET SKUS
        getSkuSuccess(state, action) {
            state.skus = action.payload;
        },
        getCategoriesSuccess(state, action) {
            state.categories = action.payload;
        },
        // GET RELATED PRODUCTS
        getRelatedProductsSuccess(state, action) {
            state.relatedProducts = action.payload;
        },
        // GET PRODUCT REVIEWS
        getProductReviewsSuccess(state, action) {
            state.reviews = action.payload;
        },
        //Get Files
        getFilesSuccess(state, action) {
            state.files = action.payload;
        },
        getImageSuccess(state, action) {
            state.image = action.payload;
        },
        clearUrlSearch(state) {
            state.urlSearch = '';
        }
    },
    extraReducers(builder) {
        builder
            .addCase(saveProduct.pending, (state) => {
                state.creatingProduct = true;
            })
            .addCase(saveProduct.fulfilled, (state) => {
                state.creatingProduct = false;
            });

        builder
            .addCase(approvalStatus.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(approvalStatus.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.approvalStatus = action.payload.response.skuApprovalStatus;
            });

        builder
            .addCase(getRejectedStatus.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getRejectedStatus.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.getRejectedStatus = action.payload.response;
            });

        builder
            .addCase(saveApprovalStatus.pending, (state) => {
                state.savingApproval = true;
            })
            .addCase(saveApproveAll.pending, (state) => {
                state.savingApproval = true;
            })
            .addCase(saveApprovalStatus.fulfilled, (state) => {
                state.savingApproval = false;
            })
            .addCase(saveApproveAll.fulfilled, (state) => {
                state.savingApproval = false;
            });
        builder
            .addCase(getApprovalHistorial.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getApprovalHistorial.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.approvalHistorial = action.payload.response;
            });
        builder
            .addCase(getSku.pending, (state) => {
                state.loadingSku = true;
            })
            .addCase(getSku.fulfilled, (state, action) => {
                state.loadingSku = false;
                state.selectedSku = action.payload.response;
            });
        builder
            .addCase(getSkusMulticatalogService.pending, (state) => {
                state.loadingSkus = true;
            })
            .addCase(getSkusMulticatalogService.fulfilled, (state, action) => {
                state.loadingSkus = false;
                state.selectedSkus = action.payload.response;
            });
        /* ------------------------------------------- */
        /* --------- MULTI MERCHANT SERVICES --------- */
        /* ------------------------------------------- */
        builder
            // PRODUCT
            .addCase(getProductDetails.pending, (state) => {
                state.loadingProduct = true;
            })
            .addCase(getProductDetails.fulfilled, (state, action) => {
                state.loadingProduct = false;
                state.merchantProducts = action.payload;
            });
        // SKUs
        builder
            .addCase(getProductSkuList.pending, (state) => {
                state.loadingProduct = true;
            })
            .addCase(getProductSkuList.fulfilled, (state) => {
                state.loadingProduct = false;
            });
        builder
            .addCase(getWarehouses.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getWarehouses.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.warehouses = action.payload.response;
            });

        builder
            .addCase(uploadOneImageToSku.pending, (state, action) => {
                state.uploadedImages.pending.push(action.meta.arg.file.name);
            })
            .addCase(uploadOneImageToSku.fulfilled, (state, action) => {
                state.uploadedImages.done.push({ ...action.payload.data.response, name: action.meta.arg.file.name });
            })
            .addCase(uploadOneImageToSku.rejected, (state, action) => {
                state.uploadedImages.error.push({
                    skuId: action.meta.arg.skuId,
                    name: action.meta.arg.file.name,
                    error: JSON.stringify(action?.error ?? { error: 'Unknown error' })
                });
            })

            .addCase(resetUploadedImages, (state) => {
                state.uploadedImages = { pending: [], done: [], error: [] };
                state.totalImagesToLoad = 0;
            })
            .addCase(setTotalImagesToLoad, (state, action) => {
                state.totalImagesToLoad = action.payload;
            });

        builder
            .addCase(saveFacetsProducts.pending, (state) => {
                state.loadingMedia = true;
            })
            .addCase(saveFacetsProducts.fulfilled, (state) => {
                state.loadingMedia = false;
            });
        builder.addCase(getTradePolicies.fulfilled, (state, action) => {
            state.tradePolicies = action.payload;
        });
        builder
            .addCase(getProductsMulticatalog.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getProductsMulticatalog.fulfilled, (state, action) => {
                state.productsMulticatalog = action.payload.response.map((item: ProductMultiCatalog) => ({
                    ...item,
                    title: item.productName
                }));
                state.loadingProducts = false;
                state.productsTotalPages = action.payload.totalPages;
            });
        builder
            .addCase(getProductsToApprove.pending, (state) => {
                state.productsToApprove.loading = true;
            })
            .addCase(getProductsToApprove.fulfilled, (state, action) => {
                state.productsToApprove.products = action.payload.data.reduce((acc: any, el: any) => {
                    const skusWithCategoryAndProvider = el.skus.map((sku: any) => ({
                        ...sku,
                        category: el.CategoriaId,
                        proveedorName: el.ProveedorName
                    }));
                    return acc.concat(skusWithCategoryAndProvider);
                }, []);
                state.productsToApprove.totalCount = state.productsToApprove.products.length;
                state.productsToApprove.totalPages = action.payload.headers['x-total-pages'];
                state.productsToApprove.loading = false;
            })
            .addCase(getProductsToApproveCount.fulfilled, (state, action) => {
                state.productsToApproveCount = action.payload;
            });
        builder
            .addCase(priceWhenCreateProduct.pending, (state) => {
                state.loadingMedia = true;
            })
            .addCase(priceWhenCreateProduct.fulfilled, (state) => {
                state.loadingMedia = false;
            });
        builder
            .addCase(getFiles.pending, (state) => {
                state.loadingFiles = true;
            })
            .addCase(getFiles.fulfilled, (state) => {
                state.loadingFiles = false;
            })
            .addCase(getFiles.rejected, (state) => {
                state.loadingFiles = false;
            });
        builder
            .addCase(getProductErrors.pending, (state, action) => {
                state.loadingErrors = true;
            })
            .addCase(getProductErrors.fulfilled, (state, action) => {
                state.productErrors = action.payload.data;
                state.loadingErrors = false;
            });
        builder
            .addCase(resetProductErrors.pending, (state, action) => {
                state.loadingResetErrors = true;
            })
            .addCase(resetProductErrors.fulfilled, (state, action) => {
                state.productErrors = { product: [], sku: [] };
                state.loadingResetErrors = false;
            });
        builder
            .addCase(createFile.pending, (state) => {
                state.loadingFiles = true;
            })
            .addCase(createFile.fulfilled, (state) => {
                state.loadingFiles = false;
            })
            .addCase(createFile.rejected, (state) => {
                state.loadingFiles = false;
            });
        builder
            .addCase(getProductsThunkHistory.pending, (state) => {
                state.loadingBulk = true;
            })
            .addCase(getProductsThunkHistory.fulfilled, (state, action) => {
                state.bulk = action.payload;
                state.loadingBulk = false;
            });
    }
});

export default slice.reducer;

// ----------------------------------------------------------------------

export const getProductsToApprove = createAsyncThunk(
    `${slice.name}/getProductsToApprove`,
    async ({ idMerchant, statusId, email, categoryId, approvalStatusId, brandId, vendorId }: getProductsToApproveProps) => {
        const response = await axios.get(`/loolu/api/approval/`, {
            params: {
                merchantId: idMerchant,
                statusId,
                email,
                categoryId,
                brandId,
                approvalStatusId,
                vendorId
            }
        });
        const { data, headers } = response;
        return { data, headers };
    }
);

export const getProductsToApproveCount = createAsyncThunk(
    `${slice.name}/getProductsToApproveCount`,
    async ({ statusId, email, providerId }: getProductsToApproveCountProps) => {
        const response = await axios.get(`/loolu/api/approval/total`, {
            params: {
                statusId,
                email,
                providerId
            }
        });

        return response.data.total;
    }
);

export const getProductsThunk = createAsyncThunk(
    `${slice.name}/getProducts`,
    async ({ idMerchant, page, productName, idSKU, idBrand, idCategory, eans }: getProductsProps) => {
        const response = await axios.get(`/loolu/api/massive/search/`, {
            params: {
                idMerchant: idMerchant || 1,
                page,
                productName: productName || null,
                idSKU,
                idBrand,
                idCategory,
                eans
            }
        });
        return response.data;
    }
);

export const getProductsThunkHistory = createAsyncThunk(
    `${slice.name}/getProductsThunkHistory`,
    async ({ idMerchant }: getProductsThunkHistoryProps) => {
        const response = await axios.get(`/loolu/api/bulk`, {
            params: {
                merchantId: idMerchant || 1
            }
        });
        return response.data;
    }
);

export const getProductsMulticatalogVendors = async ({ idMerchant, providerId, page }: SearchProductTypeProps) => {
    const response = await axios.get('/loolu/api/multicatalog/product/search', {
        params: {
            idMerchant,
            providerId,
            page,
            limit: 999
        }
    });
    return response.data;
};
export const getProductsFilterResults = async ({
    refID,
    textLink,
    idMerchant,
    providerId,
    productName,
    ean,
    productRefID,
    idSKU,
    idProd,
    categoryId,
    brandId,
    page,
    limit
}: SearchProductTypeProps) => {
    const response = await axios.get('/loolu/api/multicatalog/product/search', {
        params: {
            refID,
            textLink,
            idMerchant,
            providerId,
            productName,
            ean,
            productRefID,
            idSKU,
            idProd,
            categoryId,
            brandId,
            page,
            limit: limit || 12
        }
    });
    return response.data;
};

export const getProductMultiCatalogURL = '/loolu/api/multicatalog/product/search';

export const getProductsMulticatalog = createAsyncThunk(
    `${slice.name}/getProductsMulticatalog`,
    async ({
        refID,
        textLink,
        idMerchant,
        providerId,
        productName,
        ean,
        productRefID,
        idSKU,
        idProd,
        categoryId,
        brandId,
        page,
        limit
    }: SearchProductTypeProps) => {
        const response = await axios.get(getProductMultiCatalogURL, {
            params: {
                refID,
                textLink,
                idMerchant,
                providerId,
                productName,
                ean,
                productRefID,
                idSKU,
                idProd,
                categoryId,
                brandId,
                page,
                limit: limit || 15
            }
        });
        return response.data;
    }
);

export const getCategoryFacets = createAsyncThunk(
    `${slice.name}/getCategoryFacets`,
    async ({ merchantId, categoryId }: getCategoryFacetsProps) => {
        const response = await axios.get(`/loolu/api/catalog/facets/fv/merchant/${merchantId}/category/${categoryId}`);
        return response.data;
    }
);

export const saveFacetsProducts = createAsyncThunk(`${slice.name}/saveFacetsProducts`, async (data: SaveFacetsProductsProps) => {
    const { merchantId, productId, dataFacets } = data;

    const response = await axios.post(`/loolu/api/linkvariant/product`, dataFacets, {
        params: {
            idMerchant: merchantId,
            productId
        }
    });
    return response.data;
});

export const saveFacetsSkus = createAsyncThunk(`${slice.name}/saveFacetsSkus`, async (data: SaveFacetsSkusProps) => {
    const { merchantId, skuId, dataFacets } = data;

    const response = await axios.post(`/loolu/api/linkvariant/sku`, dataFacets, {
        params: {
            idMerchant: merchantId,
            skuId
        }
    });
    return response.data;
});

const retryService = async (idSKU: number, idMerchant: number, reintentos: number): Promise<any> => {
    const maxRetries = 5;
    if (reintentos >= maxRetries) {
        throw new Error('Se alcanzó el límite de reintentos');
    }

    const response = await axios.get('/loolu/api/product/detail/sku', {
        params: {
            idMerchant,
            idSKU
        }
    });
    if (response.data.code === 200) {
        reintentos = 0;
        return response;
    }
    reintentos += 1;
    return retryService(idSKU, idMerchant, reintentos);
};

export const getSku = createAsyncThunk(`${slice.name}/getSku`, async ({ idSKU, idMerchant }: any): Promise<any> => {
    const reintentos = 0;
    const response = await retryService(idSKU, idMerchant, reintentos);
    return response?.data;
});

export const saveProduct = createAsyncThunk(
    `${slice.name}/saveProduct`,
    async (productBody: AllMerchantsProductDataType<CreateDetailProductType>) => {
        try {
            const response = await axios.post('/loolu/api/product/save/product', productBody.detailProduct, {});
            setTimeout(() => {}, 2000);
            return response.data;
        } catch (e: any) {
            return e;
        }
    }
);

export const getTradePolicies = createAsyncThunk(`${slice.name}/getTradePolicies`, async (idMerchant: number) => {
    try {
        const response = await axios.get('/loolu/api/multicatalog/tradepolicies', {
            params: {
                idMerchant
            }
        });
        return response.data.response;
    } catch (error: any) {
        return error.response.data;
    }
});

export const approvalStatus = createAsyncThunk(`${slice.name}/lifecycleapproval`, async (idMerchant: number | null) => {
    const response = await axios.get('/loolu/api/product/lifecycleapproval', {
        params: {
            idMerchant: idMerchant
        }
    });
    return response.data;
});

export const getRejectedStatus = createAsyncThunk(`${slice.name}/rejectedstatus`, async ({ idMerchant }: { idMerchant: number }) => {
    const response = await axios.get('/loolu/api/product/rejectcatalog', {
        params: {
            idMerchant: idMerchant || 1
        }
    });
    return response.data;
});

export const saveApprovalStatus = createAsyncThunk(
    `${slice.name}/saveApprovalStatus`,
    async ({ idMerchant, saveApprovalPayload }: { idMerchant: number; saveApprovalPayload: SaveApprovalPayload }) => {
        const response = await axios.post('/loolu/api/product/save/approval', saveApprovalPayload, {
            params: {
                idMerchant
            }
        });

        saveApprovalPayload.isLast &&
            (await axios.post('/loolu/api/product/save/approval', saveApprovalPayload, {
                params: {
                    idMerchant
                }
            }));
        return response.data;
    }
);

export const saveApproveAll = createAsyncThunk(`${slice.name}/saveApproveAll`, async (payload: any) => {
    const response = await axios.post('/loolu/api/multicatalog/product/save/approve', [payload], {});
    return response.data;
});

export const getApprovalHistorial = createAsyncThunk(
    `${slice.name}/approvalhistorial`,
    async ({ skuId, prodId, idMerchant }: { skuId: string | null; prodId: string | number | undefined; idMerchant: any }) => {
        const response = await axios.get('/loolu/api/product/approval', {
            params: {
                skuId,
                prodId,
                idMerchant
            }
        });
        return response.data;
    }
);

/* ------------------------------------------- */
/* --------- MULTI MERCHANT SERVICES --------- */
/* ------------------------------------------- */

export const getProductDetails = createAsyncThunk(
    `${slice.name}/getMultiMerchantProduct`,
    async ({
        idProd,
        merchants,
        availableMerchantsForProduct
    }: {
        idProd: number | string;
        merchants: MerchantType[];
        availableMerchantsForProduct: Partial<MerchantType>[];
    }) => {
        const resolvedPromises: any = [];
        try {
            for (const { merchantId } of merchants) {
                if (!availableMerchantsForProduct.find((availableMerchant) => availableMerchant.merchantId === merchantId)) continue;

                // eslint-disable-next-line no-await-in-loop
                const skuListResult = await axios.get(`/loolu/api/product/detail/productSkus`, {
                    params: {
                        idProd,
                        merchantId
                    }
                });

                const originalSkus = skuListResult.data?.response?.skus;

                if (originalSkus?.length) {
                    const filteredSkus = originalSkus.filter((sku: any) => sku.skuID !== '0');
                    skuListResult.data.response.skus = filteredSkus;
                }

                resolvedPromises.push(skuListResult);

                if (originalSkus?.length) {
                    const filteredSkus = originalSkus.filter((sku: any) => sku.skuID !== '0');
                    skuListResult.data.response.skus = filteredSkus;
                }

                resolvedPromises.push(skuListResult);
            }

            const result: MerchantProductType[] = resolvedPromises.map(({ data: { response } }: any) => {
                const foundMerchant = merchants.find((m) => m.merchantId === response.merchantId);
                if (!foundMerchant) return [];

                return {
                    merchantId: foundMerchant.merchantId,
                    merchantName: foundMerchant.name,
                    isFather: foundMerchant.isFather,
                    detailProduct: { ...response, isfatherMerchantId: foundMerchant.isFather }
                };
            });

            const cleanedResults = result.filter((item) => {
                if (!item.merchantId) return false;
                return true;
            });

            const filteredResults: MerchantProductType[] = [];

            cleanedResults.forEach((item) => {
                if (!filteredResults.find((itemA) => itemA.merchantId === item.merchantId)) {
                    filteredResults.push(item);
                }
            });

            return filteredResults;
        } catch (error) {
            return [];
        }
    }
);

export const getSkusMulticatalogService = createAsyncThunk(
    `${slice.name}/getSkusMulticatalogService`,
    async ({ idSku, idMerchant }: { idSku: number | string; idMerchant: number | string }) => {
        const response = await axios.get('/loolu/api/product/detail/sku-multicatalog', {
            params: {
                idSKU: idSku,
                idMerchant
            }
        });

        return response.data;
    }
);

export const getProductSkuList = createAsyncThunk(
    `${slice.name}/getMultiMerchantProductSKUs`,
    async ({ idProd, merchantId }: { idProd: number | string; merchantId: number | string }) => {
        const response = await axios.get('/loolu/api/product/detail/productSkus', {
            params: {
                idProd,
                merchantId
            }
        });
        return response.data.response;
    }
);

export const getWarehouses = createAsyncThunk(
    `${slice.name}/getMultiMerchantWarehouses`,
    async ({ warehouses, idMerchant }: { warehouses?: number | string; idMerchant: number | string }) => {
        const response = await axios.get('/loolu/api/multicatalog/warehouses', {
            params: {
                idMerchant,
                warehouses
            }
        });

        return response.data;
    }
);

export const updateInventoriesService = createAsyncThunk(
    `${slice.name}/updateInventories`,
    async ({ inventories }: { inventories: MakeRequired<InventorySkuType, 'stockInVTEX' | 'refID'>[] }) => {
        const response = await axios.post('/loolu/api/inventory', inventories, {});

        return response.data;
    }
);

export const getFiles = createAsyncThunk(
    `${slice.name}/getFiles`,
    async ({
        productId,
        firstLevelRoute,
        merchantId,
        secondLevelRoute
    }: {
        productId: string | number;
        firstLevelRoute?: string;
        merchantId: number;
        secondLevelRoute?: string;
    }) => {
        const response = await axios.get(
            `/loolu/api/files/${productId}${firstLevelRoute ? `/${firstLevelRoute}` : ''}${
                secondLevelRoute ? `/${secondLevelRoute}` : ''
            }?merchantId=${merchantId}`
        );

        dispatch(slice.actions.getFilesSuccess(response.data));

        return response.data;
    }
);

export const getProductErrors = createAsyncThunk(
    `${slice.name}/getProductErrors`,
    async ({ merchantId, productId }: { merchantId: string | number; productId?: string }) => {
        const response = await axios.get(
            productId
                ? `/loolu/api/catalog/products/${productId}/errors?merchantId=${merchantId}`
                : `/loolu/api/catalog/products/errors?merchantId=${merchantId}`
        );

        return response;
    }
);

export const resetProductErrors = createAsyncThunk(
    `${slice.name}/resetProductErrors`,
    async ({ merchantId, productId }: { merchantId: string | number | null; productId?: string }) => {
        const response = await axios.delete(`/loolu/api/catalog/products/${productId}/errors?merchantId=${merchantId}`);

        return response;
    }
);

export const getImage = createAsyncThunk(
    `${slice.name}/downloadFiles`,
    async ({ path, merchantId }: { path: string; merchantId: string | number }) => {
        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`, {
            responseType: 'arraybuffer'
        });

        const base64 = btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));

        dispatch(slice.actions.getImageSuccess(base64));
    }
);

export const editMetadata = createAsyncThunk(
    `${slice.name}/editMetadata`,
    async ({ path, merchantId, metadata }: { path: string; merchantId: string | number | null; metadata: metadata }) => {
        await axios.patch(`/loolu/api/files/${path}?merchantId=${merchantId}`, metadata);
    }
);

export const downloadFilesSlice = createAsyncThunk(
    `${slice.name}/downloadFiles`,
    async ({ path, merchantId }: { path: string; merchantId: string | number }) => {
        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`, {
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${path.split('/').pop()}`);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
);

export const downloadFilesZip = createAsyncThunk(
    `${slice.name}/downloadFiles`,
    async ({ paths, merchantId }: { paths: any[]; merchantId: string | number }) => {
        const zip = new JSZip();

        await Promise.all(
            paths.map(async (path) => {
                const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`, {
                    responseType: 'blob'
                });

                const blob = await response.data;
                zip.file(`${path.split('/').pop()}`, blob);
            })
        );

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(zipBlob);
        a.download = 'images.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
);

export const deleteFilesSlice = createAsyncThunk(
    `${slice.name}/deleteFiles`,
    async ({ path, merchantId }: { path: string; merchantId: string | number }) => {
        const response = await axios.delete(`/loolu/api/files/${path}?merchantId=${merchantId}`);

        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const createFolderSlice = createAsyncThunk(
    `${slice.name}/createFolder`,
    async ({ path, merchantId, directoryName }: { path: string; merchantId: string | number; directoryName: string }) => {
        await axios.post(`/loolu/api/files/${path}?merchantId=${merchantId}`, { directoryName });

        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`);

        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const createFile = createAsyncThunk(
    `${slice.name}/createFile`,
    async ({ path, merchantId, fileList }: { path: string; merchantId: string | number; fileList: FileList }) => {
        const files = Array.from(fileList);
        const formData = new FormData();

        files.forEach((file) => {
            formData.append('files', file);
        });

        const metadata = files.map((file) => ({ id: file.name }));
        formData.append('metadata', JSON.stringify(metadata));

        await axios.post(`/loolu/api/files/${path}?merchantId=${merchantId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`);
        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const resetUploadedImages = createAction('product/resetUploadedImages');
export const setTotalImagesToLoad = createAction('product/setTotalImagesToLoad', (totalImagesToLoad: number) => ({
    payload: totalImagesToLoad
}));

export const updateImagesToSku = createAsyncThunk(
    `${slice.name}/updateImagesToSku`,
    async ({
        images,
        skuId,
        merchantId
    }: {
        images: { IdImage: number; ImageURL?: string }[];
        skuId: number | string;
        merchantId: number;
    }) => {
        const imagesList = images.map((item, index) => ({
            // ...item,
            IdImage: item.IdImage,
            Label: item.ImageURL ?? `${skuId}-${index}`,
            Text: item.ImageURL ?? `${skuId}-${index}`
        }));

        const uploadResponse = await axios.post(`/loolu/api/files/img/${skuId}`, imagesList, {
            params: {
                merchantId
            }
        });

        return uploadResponse;
    }
);

export const uploadOneImageToSku = createAsyncThunk(
    `${slice.name}/uploadOneImageToSku`,
    async ({ skuId, idMerchant, file, isMain = false }: uploadOneImageToSkuProps) => {
        const fileFormData = new FormData();

        // Test errors
        // eslint-disable-next-line no-nested-ternary
        const fileToUpload = file;

        fileFormData.append('isMain', `${isMain}`);
        fileFormData.append('skuId', `${skuId}`);
        fileFormData.append('file', fileToUpload);
        fileFormData.append('idMerchant', `${idMerchant}`);
        fileFormData.append('imageName', file.name);
        fileFormData.append('label', new Date().toISOString());

        const uploadResponse = await axios.post('/loolu/api/images/save/file', fileFormData, {});

        return uploadResponse;
    }
);

export const deleteOneImageToSku = createAsyncThunk(`${slice.name}/deleteImage`, async (image: skuImageType) => {
    const deleteResponse = await axios.delete('/loolu/api/images/delete/file', {
        params: {
            idImage: image.IdImage,
            idMerchant: image.IdMerchant,
            idSKU: image.SkuID
        }
    });

    return deleteResponse;
});

export const updatePricingService = createAsyncThunk(`${slice.name}/updatePrice`, async ({ prices }: { prices: PricingItemType[] }) => {
    const pricesData = prices.map((item) => ({
        idMerchant: Number(item.idMerchant),
        price: Number(item.price) ?? 1,
        priceDiscount: Number(item.priceDiscount) ?? 1,
        refID: Number(item.skuId),
        tradePolicy: item.tradePolicy
    }));

    const response = await axios.post('/loolu/api/price', pricesData, {});

    return response.data;
});

export const saveMultipleSKU = createAsyncThunk(
    `${slice.name}/saveMultipleProducts`,
    async ({ products }: { products: { [key: string]: any }[] }) => {
        // MODIFY TO SAVE SKU
        const requests = products.map((detailProduct: any) => {
            const product = {
                ...detailProduct,
                sku: {
                    ...detailProduct.sku
                }
            };

            return axios.post('/loolu/api/product/save/product', product, {});
        });

        const responses = await Promise.all(requests);
        return responses.map((response) => response.data);
    }
);

type SaveProductResponse = {
    code: number;
    status: string | null;
    message: string;
    response: {
        skuID: string;
        prodID: string;
    };
};

export const saveMultipleProducts = createAsyncThunk(
    `${slice.name}/saveMultipleProducts`,
    async (products: AllMerchantsProductDataType<EditDetailProductType | CreateDetailProductType>[]) => {
        // async (products: AllMerchantsProductDataType<EditDetailProductType | CreateDetailProductType>[]) => {
        const requests = products.map(({ detailProduct }) => {
            const productData = { ...detailProduct, departmentId: detailProduct?.categoryId };

            return axios.post<SaveProductResponse>('/loolu/api/product/save/product', productData, {});
        });
        const responses = await Promise.allSettled(requests);

        const successfulResponses = responses
            .filter((response) => response.status === 'fulfilled')
            .map((response) => (response as PromiseFulfilledResult<AxiosResponse<SaveProductResponse, any>>).value.data);

        const failedProducts = products.filter((_, index) => responses[index].status === 'rejected');

        if (failedProducts.length) {
            const errorMessage = failedProducts.reduce((acc, el) => {
                if (acc !== 'Product update failed at merchant ') {
                    acc += ', ';
                }

                acc += el.merchantName;
                return acc;
            }, 'Product update at merchant ');

            snackbarShow(errorMessage, 'error');
        }

        return successfulResponses;
    }
);

export const associateProduct = async ({
    merchantId,
    productId,
    idProvider,
    action
}: {
    merchantId: number | string;
    productId: number | string;
    idProvider: number | string;
    action: string;
}) => {
    try {
        const response =
            action === 'remove'
                ? await axios.delete(`/loolu/api/catalog/products/${productId}/vendor`, {
                      params: {
                          merchantId
                      }
                  })
                : await axios.put(`/loolu/api/catalog/products/${productId}/vendor`, {
                      merchantId,
                      productId,
                      idProvider
                  });

        return response;
    } catch (error) {}
};

export const priceWhenCreateProduct = createAsyncThunk(`${slice.name}/priceWhenCreateProduct`, async (prices: PricingItemType[]) => {
    const response = await axios.post('/loolu/api/price', prices, {});

    return response;
});

const snackbarShow = (message: string | unknown, variant: 'success' | 'error') => {
    dispatchSnackBar(
        openSnackbar({
            message,
            variant
        })
    );
};
